import { Procedure, Address, Insurance } from "@/types/procedure";
import { BooleanMutation } from "@/utils/helpers";
import {
  AddressContentWrapper,
  AddressLineTwo,
  AddressText,
  AddressWrapper,
  Row,
} from "./components.stitches";
import InsuranceCard from "./InsuranceCard";
import { DateTime } from "luxon";

/**
 * Generates the authorization options for a procedure.
 * Each status is mapped to a label/value pair, and 'Obtained' is disabled if no authorization number exists.
 * @param procedure - The procedure object.
 * @returns An array of authorization options.
 */
export const generateAuthOptions = (procedure: Procedure) => {
  return ["Pending", "Obtained", "Not Required", "Closed"].map((status) => ({
    label: status,
    value: status,
    disabled: status === "Obtained" && !procedure?.authInfo.authorizationNumber,
  }));
};

/**
 * Formats the address object into a formatted address string, with optional highlighting for diffs.
 * @param address - The address object.
 * @param addressDiff - Optional. The address mutation object for highlighting differences.
 * @returns The formatted address JSX element.
 */
export const addressFormatter = (
  { line1, city, state, zip }: Address,
  addressDiff?: BooleanMutation<Address>,
) => {
  return (
    <AddressWrapper>
      <AddressText highlight={addressDiff && addressDiff["line1"]}>
        <AddressContentWrapper>{line1}</AddressContentWrapper>
      </AddressText>
      <AddressLineTwo>
        <AddressText highlight={addressDiff && addressDiff["city"]}>
          <AddressContentWrapper>{city},</AddressContentWrapper>
        </AddressText>
        <AddressText highlight={addressDiff && addressDiff["state"]}>
          <AddressContentWrapper>{state}</AddressContentWrapper>
        </AddressText>
        <AddressText highlight={addressDiff && addressDiff["zip"]}>
          <AddressContentWrapper>{zip}</AddressContentWrapper>
        </AddressText>
      </AddressLineTwo>
    </AddressWrapper>
  );
};

/**
 * Renders the insurance cards for a list of insurances.
 * Supports configuration for icons, badges, click handlers, and empty state.
 * @param insurances - The array of insurance objects.
 * @param config - Optional. The configuration object for rendering the insurance cards.
 * @returns The rendered insurance cards JSX element.
 */
export const renderInsurances = (
  insurances: Insurance[],
  config?: {
    icon?: React.ReactNode;
    onIconClick?: (insurance: Insurance) => void;
    onEmpty?: React.ReactNode;
    iconDisabled?: boolean;
    badge?: React.ReactNode;
    defaultIsOpen?: boolean;
    mini?: boolean;
    disableAlert?: boolean;
  },
) => {
  return (
    <Row
      css={{
        flexWrap: "wrap",
      }}
    >
      {insurances.length > 0
        ? insurances.map((insurance) => (
            <InsuranceCard
              insurance={insurance}
              config={config}
              key={insurance.insuranceNumber}
            />
          ))
        : config?.onEmpty}
    </Row>
  );
};

/**
 * Calculates the age based on the date of birth string.
 * @param dob - The date of birth string in the format "yyyy-mm-dd".
 * @returns The calculated age as a number, or null if the date of birth is not provided.
 */
export const getAge = (dob: string | undefined): number | null => {
  if (!dob) return null;
  const diff = DateTime.fromFormat(dob, "yyyy-mm-dd").diffNow("years");
  return Math.floor(Math.abs(diff.years));
};
